import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';

import MenuIcon from '@mui/icons-material/Menu';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Wifi from '@mui/icons-material/Wifi';

const SHOP_SETTINGS_CACHE_TTL_MS = 60000;
let shopSettingsNameCache = '';
let shopSettingsNameCachedAt = 0;
let shopSettingsNameInFlight = null;

const TopBar = ({ onMenuClick, drawerOpen = true }) => {
  const { user, logout, verificationPending, verificationFailed } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [companyName, setCompanyName] = React.useState('BUZZKILL ISP');

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const now = Date.now();
        if (shopSettingsNameCache && (now - shopSettingsNameCachedAt) <= SHOP_SETTINGS_CACHE_TTL_MS) {
          if (active) setCompanyName(shopSettingsNameCache);
          return;
        }

        if (!shopSettingsNameInFlight) {
          shopSettingsNameInFlight = (async () => {
            const res = await api.get('/shop/bootstrap', { params: { view: 'entitlements' }, timeout: 30000, meta: { readRetries: 0 } });
            const payload = res?.data?.data || {};
            const nextName = String(
              payload?.name
              || payload?.company?.name
              || payload?.shop?.name
              || ''
            ).trim();
            if (nextName) {
              shopSettingsNameCache = nextName;
              shopSettingsNameCachedAt = Date.now();
            }
            return nextName;
          })().finally(() => {
            shopSettingsNameInFlight = null;
          });
        }

        const nextName = await shopSettingsNameInFlight;
        if (active && nextName) setCompanyName(nextName);
      } catch (_) {
        // Keep default title when settings are unavailable.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const effectiveRole = React.useMemo(() => {
    const raw = String(user?.shop_roles || user?.role || '').trim();
    if (!raw) return 'User';
    return raw
      .split(',')[0]
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }, [user?.role, user?.shop_roles]);

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const handleSettings = () => {
    navigate('/shop?module=settings&section=company');
    handleUserMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        boxShadow: 1,
      }}
    >
      <Toolbar>
        {typeof onMenuClick === 'function' && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label={drawerOpen ? 'close navigation drawer' : 'open navigation drawer'}
            aria-expanded={Boolean(drawerOpen)}
            aria-controls="app-navigation-drawer"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Wifi sx={{ fontSize: 26, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {companyName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {verificationPending && verificationFailed && (
            <Typography variant="caption" color="warning.main" role="status">
              Session not verified — check connection
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="user-menu"
              aria-haspopup="true"
              onClick={handleUserMenuOpen}
              color="inherit"
            >
              {user?.avatar ? (
                <Avatar src={user.avatar} sx={{ width: 32, height: 32 }} />
              ) : (
                <AccountCircle />
              )}
            </IconButton>
          </Box>
        </Box>

        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleUserMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {effectiveRole}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleSettings}>
            <Settings sx={{ mr: 1 }} />
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <Logout sx={{ mr: 1 }} />
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;